import React, {useMemo, useState} from "react";
import {Box, LinearProgress, Menu, MenuItem} from "@mui/material";
import {useQuery} from "@apollo/client";
import moment from "moment-timezone";

import {ALL_SERVICE_TYPES, SHIFT_ADMIN_PERFORMER_ACTIVITY} from "../queries/GraphQLQueries";
import {useTimezone} from "../hooks/useTimezone";
import {
    calculateNetAmendmentDurationInMinutes,
    calculateTotalShiftDurationIncludingCheckInTimeInMinutes
} from "../helpers/ShiftHelper";
import {buildMonthDateRangeIso} from "../helpers/DateTimeRangeHelper";
import ShiftAdminUtils from "../helpers/ShiftAdminUtils";
import {SHIFT_ADMIN_ALL_FILTER_VALUE, SHIFT_ADMIN_VIEWS} from "../constants/ShiftAdminConstants";
import {YEAR_MONTH_FORMAT} from "../config/DateTimeConstants";
import ShiftAdminToolbar from "../components/shift-admin/ShiftAdminToolbar";
import ShiftAdminSummaryHeader from "../components/shift-admin/ShiftAdminSummaryHeader";
import ShiftAdminPerformerGrid from "../components/shift-admin/ShiftAdminPerformerGrid";
import ShiftAdminAddAmendmentDialog from "../components/shift-admin/ShiftAdminAddAmendmentDialog";

const EARLIEST_SHIFT_HISTORY_MONTH = process.env.REACT_APP_EARLIEST_SHIFT_HISTORY_MONTH;

const styles = {
    page: {
        px: 2,
        pb: 1
    },
    progressTrack: {
        width: '100%',
        height: '0.25rem',
    }
};

export default function ShiftAdminView() {
    const {timezone} = useTimezone();
    const [activeView, setActiveView] = useState(SHIFT_ADMIN_VIEWS.MONTH);
    const [currentDate, setCurrentDate] = useState(moment().tz(timezone));
    const [selectedServiceFilters, setSelectedServiceFilters] = useState([]);
    const [selectedPerformerFilter, setSelectedPerformerFilter] = useState(SHIFT_ADMIN_ALL_FILTER_VALUE);
    const [addPerformerMenuAnchorElement, setAddPerformerMenuAnchorElement] = useState(null);
    const [openAddAmendmentDialog, setOpenAddAmendmentDialog] = useState(false);
    const [selectedPerformer, setSelectedPerformer] = useState(null);
    const earliestNavigableDate = useMemo(() => moment.tz(EARLIEST_SHIFT_HISTORY_MONTH, YEAR_MONTH_FORMAT, timezone).startOf("month"), [timezone]);

    const {startTime: fromTime, endTime: toTime} = useMemo(
        () => buildMonthDateRangeIso(currentDate, timezone),
        [currentDate, timezone]
    );

    const {data: serviceTypesData} = useQuery(ALL_SERVICE_TYPES);
    const {data, loading, refetch} = useQuery(SHIFT_ADMIN_PERFORMER_ACTIVITY, {variables: {fromTime, toTime, includeOnlyAccreditedServiceTypes: true}, fetchPolicy: "network-only"});

    const performerRows = useMemo(() => {
        const users = data?.users_filtered || [];

        return users.map(user => {
            const monthShifts = (user.availability || []).filter(shift => {
                const shiftStartTime = shift?.from;
                return shiftStartTime && moment(shiftStartTime).tz(timezone).isSame(currentDate, "month");
            });

            const monthStandaloneAmendments = (user.amendments || []).filter(amendment => !amendment?.shift?.id);

            if (monthShifts.length === 0 && monthStandaloneAmendments.length === 0) {
                return null;
            }

            const shiftIds = monthShifts.map(shift => shift.id);
            const monthShiftAmendments = (user.amendments || []).filter(amendment => shiftIds.includes(amendment?.shift?.id));
            const monthAmendments = [...monthShiftAmendments, ...monthStandaloneAmendments];

            const shiftMinutes = calculateTotalShiftDurationIncludingCheckInTimeInMinutes(monthShifts);

            const amendmentMinutes = calculateNetAmendmentDurationInMinutes(monthAmendments);
            const serviceKeycodes = monthShifts.flatMap((shift) => ShiftAdminUtils.mapShiftServiceKeycodes(shift));

            return {
                performerId: user.id,
                fullName: `${user?.givenName} ${user?.familyName}`,
                shifts: monthShifts,
                amendments: monthAmendments,
                services: [...new Set(serviceKeycodes)],
                shiftTimeRanges: ShiftAdminUtils.buildShiftTimeRanges(monthShifts, timezone),
                totalShiftCount: monthShifts.length,
                totalAmendmentCount: monthAmendments.length,
                totalMinutesIncludingAmendments: shiftMinutes + amendmentMinutes
            };
        })
            .filter(Boolean)
            .sort((left, right) => left.fullName.localeCompare(right.fullName));
    }, [data, currentDate, timezone]);

    const serviceFilterOptions = useMemo(() => {
        const serviceTypes = serviceTypesData?.serviceTypes || [];

        return serviceTypes
            .map((serviceType) => serviceType.keycode)
            .filter(Boolean)
            .sort();
    }, [serviceTypesData]);

    const effectiveSelectedServiceFilters = useMemo(() => {
        const selectedVisibleServices = selectedServiceFilters
            .filter((serviceKeycode) => serviceFilterOptions.includes(serviceKeycode));

        return selectedVisibleServices.length > 0
            ? selectedVisibleServices
            : serviceFilterOptions;
    }, [selectedServiceFilters, serviceFilterOptions]);

    const buildEffectiveSelectedPerformerFilter = (rows) => {
        return rows.some((row) => row.performerId === selectedPerformerFilter)
            ? selectedPerformerFilter
            : SHIFT_ADMIN_ALL_FILTER_VALUE;
    };

    const buildServiceFilteredRows = (rows) => {
        if (!hasEffectiveServiceFilters) {
            return rows;
        }

        return rows.filter((row) => ShiftAdminUtils.doesRowMatchServiceFilter(row, effectiveSelectedServiceFilters));
    };

    const hasEffectiveServiceFilters = effectiveSelectedServiceFilters.length > 0;

    const filteredMonthRows = useMemo(() => {
        const serviceFilteredPerformerRows = buildServiceFilteredRows(performerRows);
        const effectiveSelectedPerformerFilter = buildEffectiveSelectedPerformerFilter(serviceFilteredPerformerRows);

        return performerRows.filter(row => {
            const serviceFilterMatches = !hasEffectiveServiceFilters
                || ShiftAdminUtils.doesRowMatchServiceFilter(row, effectiveSelectedServiceFilters);
            const performerFilterMatches = effectiveSelectedPerformerFilter === SHIFT_ADMIN_ALL_FILTER_VALUE || row.performerId === effectiveSelectedPerformerFilter;

            return serviceFilterMatches && performerFilterMatches;
        }).map(row => {
            if (!hasEffectiveServiceFilters) {
                return row;
            }

            const serviceFilteredMetrics = ShiftAdminUtils.buildServiceFilteredRowMetrics(row, effectiveSelectedServiceFilters, timezone);

            return {
                ...row,
                ...serviceFilteredMetrics,
                totalAmendmentCount: serviceFilteredMetrics.amendmentCount
            };
        });
    }, [performerRows, effectiveSelectedServiceFilters, hasEffectiveServiceFilters, selectedPerformerFilter, timezone]);

    const dayRows = useMemo(() => {
        const selectedDay = moment(currentDate).tz(timezone);

        return performerRows
            .map(row => {
                const dailyShifts = row.shifts.filter(shift => {
                    const shiftStart = shift?.from;
                    return shiftStart && moment(shiftStart).tz(timezone).isSame(selectedDay, "day");
                });

                const dailyStandaloneAmendments = row.amendments.filter(amendment => {
                    return !amendment?.shift?.id
                        && amendment?.startTime
                        && moment(amendment.startTime).tz(timezone).isSame(selectedDay, "day");
                });

                if (dailyShifts.length === 0 && dailyStandaloneAmendments.length === 0) {
                    return null;
                }

                const shiftIds = dailyShifts.map(shift => shift.id);
                const dailyShiftAmendments = row.amendments.filter(amendment => shiftIds.includes(amendment?.shift?.id));
                const dailyAmendments = [...dailyShiftAmendments, ...dailyStandaloneAmendments];

                const shiftMinutes = calculateTotalShiftDurationIncludingCheckInTimeInMinutes(dailyShifts);

                const amendmentMinutes = calculateNetAmendmentDurationInMinutes(dailyAmendments);
                const dailyServiceKeycodes = dailyShifts.flatMap((shift) => ShiftAdminUtils.mapShiftServiceKeycodes(shift));

                return {
                    performerId: row.performerId,
                    fullName: row.fullName,
                    shifts: dailyShifts,
                    services: [...new Set(dailyServiceKeycodes)],
                    amendments: dailyAmendments,
                    totalShiftCount: dailyShifts.length,
                    shiftTimeRanges: ShiftAdminUtils.buildShiftTimeRanges(dailyShifts, timezone),
                    amendmentCount: dailyAmendments.length,
                    totalMinutesIncludingAmendments: shiftMinutes + amendmentMinutes
                };
            })
            .filter(Boolean)
            .sort((left, right) => left.fullName.localeCompare(right.fullName));
    }, [performerRows, currentDate, timezone]);

    const filteredDayRows = useMemo(() => {
        const serviceFilteredDayRows = buildServiceFilteredRows(dayRows);
        const effectiveSelectedPerformerFilter = buildEffectiveSelectedPerformerFilter(serviceFilteredDayRows);

        return dayRows.filter(row => {
            const serviceFilterMatches = !hasEffectiveServiceFilters
                || ShiftAdminUtils.doesRowMatchServiceFilter(row, effectiveSelectedServiceFilters);
            const performerFilterMatches = effectiveSelectedPerformerFilter === SHIFT_ADMIN_ALL_FILTER_VALUE || row.performerId === effectiveSelectedPerformerFilter;
            return serviceFilterMatches && performerFilterMatches;
        }).map(row => {
            if (!hasEffectiveServiceFilters) {
                return row;
            }

            const serviceFilteredMetrics = ShiftAdminUtils.buildServiceFilteredRowMetrics(row, effectiveSelectedServiceFilters, timezone);

            return {
                ...row,
                ...serviceFilteredMetrics
            };
        });
    }, [dayRows, effectiveSelectedServiceFilters, hasEffectiveServiceFilters, selectedPerformerFilter, timezone]);

    const addPerformerMenuOptions = useMemo(() => {
        const users = data?.users_filtered || [];

        return ShiftAdminUtils.buildAddPerformerMenuOptions(users, filteredDayRows);
    }, [data, filteredDayRows]);

    const isPreviousDateNavigationDisabled = moment(currentDate)
        .tz(timezone)
        .isSameOrBefore(earliestNavigableDate, activeView);

    const goToPrevious = () => {
        if (isPreviousDateNavigationDisabled) {
            return;
        }

        setCurrentDate(previousDate => moment(previousDate).tz(timezone).subtract(1, activeView));
    };

    const goToNext = () => {
        setCurrentDate(previousDate => moment(previousDate).tz(timezone).add(1, activeView));
    };

    const handleDateChange = (nextDate) => {
        if (!nextDate) {
            return;
        }

        const nextActiveDate = moment(nextDate)
            .tz(timezone)
            .startOf(activeView);

        setCurrentDate(nextActiveDate);
    };

    const handleViewChange = (_, nextView) => {
        if (!nextView) {
            return;
        }

        setSelectedPerformerFilter(SHIFT_ADMIN_ALL_FILTER_VALUE);
        setActiveView(nextView);
    };

    const isMonthView = activeView === SHIFT_ADMIN_VIEWS.MONTH;
    const performersForFilterSource = isMonthView ? performerRows : dayRows;
    const serviceFilteredPerformerSourceRows = buildServiceFilteredRows(performersForFilterSource);
    const effectiveSelectedPerformerFilter = buildEffectiveSelectedPerformerFilter(serviceFilteredPerformerSourceRows);

    const performersForDropdown = serviceFilteredPerformerSourceRows
        .map(row => ({
            id: row.performerId,
            fullName: row.fullName
        }));

    const handleCloseAddAmendmentDialog = () => {
        setOpenAddAmendmentDialog(false);
        setSelectedPerformer(null);
    };

    const handleSelectAddPerformer = (performer) => {
        setSelectedPerformer(performer);
        setAddPerformerMenuAnchorElement(null);
        setOpenAddAmendmentDialog(true);
    };

    const handleServiceFilterChange = (event) => {
        const nextServiceFilters = event.target.value;

        if (nextServiceFilters.length === 0) {
            return;
        }

        setSelectedServiceFilters(nextServiceFilters);
    };

    const dateNavigation = {
        currentDate,
        onDateChange: handleDateChange,
        onGoPrevious: goToPrevious,
        onGoNext: goToNext,
        earliestNavigableDate,
        isPreviousDateNavigationDisabled
    };

    const serviceFilter = {
        selectedServiceFilters: effectiveSelectedServiceFilters,
        onServiceFilterChange: handleServiceFilterChange,
        serviceFilterOptions
    };

    const performerFilter = {
        selectedPerformerFilter: effectiveSelectedPerformerFilter,
        onPerformerFilterChange: (event) => setSelectedPerformerFilter(event.target.value),
        performersForDropdown
    };

    return (
        <Box sx={styles.page}>
            <ShiftAdminToolbar
                activeView={activeView}
                onViewChange={handleViewChange}
                dateNavigation={dateNavigation}
                serviceFilter={serviceFilter}
                performerFilter={performerFilter}/>

            <Box sx={styles.progressTrack}>
                {
                    loading
                    &&
                    <LinearProgress/>
                }
            </Box>

            <ShiftAdminSummaryHeader
                currentDate={currentDate}
                monthRows={filteredMonthRows}
                dayRows={filteredDayRows}
                activeView={activeView}
                showAddPerformerButton={!isMonthView}
                onAddPerformerClick={(event) => setAddPerformerMenuAnchorElement(event.currentTarget)}
                isAddPerformerDisabled={addPerformerMenuOptions.length === 0}/>

            <ShiftAdminPerformerGrid
                rows={isMonthView ? filteredMonthRows : filteredDayRows}
                view={activeView}
                isPerformersDataLoading={loading}
                currentDate={currentDate}
                onAmendmentCreated={refetch}/>

            <Menu
                anchorEl={addPerformerMenuAnchorElement}
                open={!!addPerformerMenuAnchorElement}
                onClose={() => setAddPerformerMenuAnchorElement(null)}>
                {addPerformerMenuOptions.map((performer) => (
                    <MenuItem key={performer.id} onClick={() => handleSelectAddPerformer(performer)}>
                        {performer.fullName}
                    </MenuItem>
                ))}
            </Menu>

            <ShiftAdminAddAmendmentDialog
                open={openAddAmendmentDialog}
                onClose={handleCloseAddAmendmentDialog}
                performer={selectedPerformer}
                timezone={timezone}
                onAmendmentCreated={refetch}/>
        </Box>
    );
}